import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, sortArrayOfObjects, loadSpecificData } from '../../../js/Helpers';
import { saveDataLineEditor } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import EditTeacherData from '../modals/edit_teacher';
import { NO_DEPARTMENT_FILTER_VALUE, prependNoDepartmentFilterOption } from '../utils/departmentFilterUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload, faMinusSquare, faSortDown, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';

const normalizeDepartmentIDs = (department_ids = []) => {
	if(!Array.isArray(department_ids)) return [];

	return department_ids.reduce((results, department_id) => {
		const normalized_department_id = `${department_id || ''}`.trim();
		if(!normalized_department_id || normalized_department_id === '0' || normalized_department_id === 'null' || normalized_department_id === 'undefined') return results;
		if(results.includes(normalized_department_id)) return results;
		results.push(normalized_department_id);
		return results;
	}, []);
};

const buildTeacherDraft = (teacher_info = {}) => ({
	teacher_id: teacher_info.teacher_id,
	teacher_code: teacher_info.teacher_code || '',
	name: teacher_info.name || '',
	first_name: teacher_info.first_name || '',
	departments: normalizeDepartmentIDs(teacher_info.departments),
});

const mergeSavedTeacherIntoList = (teacher_list = [], old_teacher_id, saved_teacher) => {
	const old_teacher_id_string = `${old_teacher_id}`;
	const new_teacher_id_string = `${saved_teacher.teacher_id}`;
	const original_teacher_index = teacher_list.findIndex((teacher_info) => `${teacher_info.teacher_id}` === old_teacher_id_string);

	if(old_teacher_id_string === new_teacher_id_string)
	{
		return teacher_list.map((teacher_info) => (
			`${teacher_info.teacher_id}` === old_teacher_id_string ? saved_teacher : teacher_info
		));
	}

	const next_teacher_list = teacher_list.filter((teacher_info) => (
		`${teacher_info.teacher_id}` !== old_teacher_id_string && `${teacher_info.teacher_id}` !== new_teacher_id_string
	));

	if(original_teacher_index === -1) return [...next_teacher_list, saved_teacher];

	const safe_insert_index = Math.min(original_teacher_index, next_teacher_list.length);
	return [
		...next_teacher_list.slice(0, safe_insert_index),
		saved_teacher,
		...next_teacher_list.slice(safe_insert_index),
	];
};

const teachersMatch = (teacher_a = {}, teacher_b = {}) => {
	const normalized_a = buildTeacherDraft(teacher_a);
	const normalized_b = buildTeacherDraft(teacher_b);

	return normalized_a.teacher_code.trim() === normalized_b.teacher_code.trim()
		&& normalized_a.name.trim() === normalized_b.name.trim()
		&& normalized_a.first_name.trim() === normalized_b.first_name.trim()
		&& JSON.stringify([...normalized_a.departments].sort()) === JSON.stringify([...normalized_b.departments].sort());
};

export default function DatabaseDataTeachers({ schoolInfo, databaseData, setDatabaseData, scheduleStructureData, scheduleInfo, students, courses, classrooms, sections, departments, dataIsLoading, handlePullNewData, readOnlyAddUpload = false, visibleDataFilter = null, departmentFilterOptions = departments, showDepartmentFilter = true}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [selectedData, setSelectedData] = useState([]);
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);

	const [sortedBy, setSortedBy] = useState('name');
	const [sortAscDesc, setSortAscDesc] = useState('asc');

	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const teacherCodeInputRef = useRef(null);
	const teacherLastNameInputRef = useRef(null);
	const teacherFirstNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	const visibleDatabaseData = useMemo(() => (
		typeof visibleDataFilter === 'function' ? databaseData.filter(visibleDataFilter) : databaseData
	), [databaseData, visibleDataFilter]);
	const departmentFilterOptionsWithNoDepartment = useMemo(() => prependNoDepartmentFilterOption(departmentFilterOptions), [departmentFilterOptions]);
	const schoolDataFilterCategories = useMemo(() => {
		const categories = [];
		if(showDepartmentFilter)
		{
			categories.push({
				key: 'departments',
				title: 'Departments',
				dataKey: 'departments',
				noValueFilterValue: NO_DEPARTMENT_FILTER_VALUE,
				options: departmentFilterOptionsWithNoDepartment.map(department_info => ({
					value: department_info.department_id,
					display: department_info.department,
				})),
			});
		}
		return categories;
	}, [departmentFilterOptionsWithNoDepartment, showDepartmentFilter]);
	const getDepartmentSortText = useCallback((department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);
		if(normalized_department_ids.length === 0) return 'No department';

		return normalized_department_ids.map(department_id => {
			const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
			return department_index !== -1 ? departments[department_index].department : 'No department';
		}).sort((department_a, department_b) => `${department_a}`.localeCompare(`${department_b}`, undefined, {numeric:true, sensitivity:'base'})).join(', ');
	}, [departments]);
	const sortByDepartmentDisplay = useCallback((teacher_list = [], sort_asc_desc = 'asc') => (
		[...(Array.isArray(teacher_list) ? teacher_list : [])].sort((teacher_a, teacher_b) => {
			const comparison = getDepartmentSortText(teacher_a.departments).localeCompare(getDepartmentSortText(teacher_b.departments), undefined, {numeric:true, sensitivity:'base'});
			return sort_asc_desc === 'asc' ? comparison : comparison * -1;
		})
	), [getDepartmentSortText]);

	const applyCurrentSort = useCallback((teacher_list = []) => (
		sortedBy === 'departments'
			? sortByDepartmentDisplay(teacher_list, sortAscDesc)
			: sortArrayOfObjects(Array.isArray(teacher_list) ? teacher_list : [], sortedBy, null, sortAscDesc)
	), [sortByDepartmentDisplay, sortedBy, sortAscDesc]);
	
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};

	const isEditingField = useCallback((teacher_id, field) => (
		editingCell
		&& `${editingCell.teacher_id}` === `${teacher_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getTeacherByID = useCallback((teacher_id) => (
		databaseData.find((teacher_info) => `${teacher_info.teacher_id}` === `${teacher_id}`) || null
	), [databaseData]);

	const startCellEdit = useCallback((teacher_info, field) => {
		if(!teacher_info) return;

		setEditingCell({teacher_id: teacher_info.teacher_id, field});
		setCellDraft(buildTeacherDraft(teacher_info));
		setIsSavingCell(false);
	}, []);

	const getDepartmentName = (department_id) => {
		const department_index = departments.findIndex((department_info) => `${department_info.department_id}` === `${department_id}`);
		return department_index !== -1 ? departments[department_index].department : 'No department';
	};

	const renderDepartmentDisplay = (department_ids = []) => {
		const normalized_department_ids = normalizeDepartmentIDs(department_ids);

		if(normalized_department_ids.length === 0)
		{
			return <div className='school-data-department medium-text'>No department</div>;
		}

		return normalized_department_ids.map((department_id, index) => (
			<div className='school-data-department medium-text' key={`${department_id}-${index}`}>{getDepartmentName(department_id)}</div>
		));
	};
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;

		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		let next_display_results = [];
		
		if(search_value.length > 1)
		{
			next_display_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['name', 'first_name', 'teacher_code']);
		}
		else
		{
			next_display_results = filteredResultsRef.current;
		}
		next_display_results = applyCurrentSort(next_display_results);
		setDisplayResults([...next_display_results]);
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [applyCurrentSort, dataIsLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			teacher_code: teacherCodeInputRef,
			name: teacherLastNameInputRef,
			first_name: teacherFirstNameInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const toggleDepartmentSelection = (department_id) => {
		const normalized_department_id = `${department_id}`;

		setCellDraft((current_draft) => {
			if(!current_draft) return current_draft;

			const department_is_selected = current_draft.departments.includes(normalized_department_id);
			const next_departments = department_is_selected
				? current_draft.departments.filter((selected_department_id) => `${selected_department_id}` !== normalized_department_id)
				: [...current_draft.departments, normalized_department_id];

			return {...current_draft, departments: next_departments};
		});
	};

	const handleSaveCell = useCallback(async (teacher_info) => {
		if(!cellDraft || !editingCell || isSavingCell) return;

		const trimmed_teacher_code = cellDraft.teacher_code ? cellDraft.teacher_code.trim() : '';
		const trimmed_last_name = cellDraft.name ? cellDraft.name.trim() : '';
		const trimmed_first_name = cellDraft.first_name ? cellDraft.first_name.trim() : '';
		const normalized_departments = normalizeDepartmentIDs(cellDraft.departments);
		const draft_to_compare = {
			...teacher_info,
			teacher_code: trimmed_teacher_code,
			name: trimmed_last_name,
			first_name: trimmed_first_name,
			departments: normalized_departments,
		};

		if(teachersMatch(teacher_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_last_name || !trimmed_first_name)
		{
			swal({
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You are missing data! Please input all background info before saving.</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);

		const original_teacher_id = teacher_info.teacher_id;
		const response_data = await saveDataLineEditor({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'teacher',
			data_to_save: {
				teacher_id: original_teacher_id,
				teacher_code: trimmed_teacher_code,
				name: trimmed_last_name,
				first_name: trimmed_first_name,
				departments: normalized_departments,
			}
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: "Unable to Save",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>We couldn&rsquo;t save this teacher value. Please try again.</p>
					</div>
				)
			});
			return;
		}

		const saved_teacher_id = response_data.new_data_id;
		let saved_teacher = {
			...teacher_info,
			teacher_id: saved_teacher_id,
			teacher_code: trimmed_teacher_code,
			name: trimmed_last_name,
			first_name: trimmed_first_name,
			departments: normalized_departments,
		};

		const refreshed_teacher = await loadSpecificData({
			data_type:'teacher',
			data_id:saved_teacher_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
		});

		if(refreshed_teacher && !refreshed_teacher.msg)
		{
			if(`${refreshed_teacher.teacher_code || ''}` !== trimmed_teacher_code)
			{
				setIsSavingCell(false);
				swal({
					title: "Unable to Verify Save",
					icon:"warning",
					buttons: {cancel: {text: "Okay", value: false, visible: true, className: 'blue-btn'}},
					content: (<div><p>The saved teacher code did not match the casing you entered. Please try again.</p></div>)
				});
				return;
			}
			saved_teacher = refreshed_teacher;
		}

		const next_database_data = mergeSavedTeacherIntoList(databaseData, original_teacher_id, saved_teacher);
		const next_filtered_results = mergeSavedTeacherIntoList(filteredResults, original_teacher_id, saved_teacher);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		setSelectedData((current_selected_data) => {
			if(`${saved_teacher_id}` === `${original_teacher_id}`) return current_selected_data;

			const next_selected_data = current_selected_data.map((selected_id) => (
				`${selected_id}` === `${original_teacher_id}` ? saved_teacher_id : selected_id
			));

			return [...new Set(next_selected_data)];
		});
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, cancelCellEdit]);

	const handleCellBlur = (teacher_info, field) => {
		if(!isEditingField(teacher_info.teacher_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(teacher_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, teacher_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(teacher_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	useEffect(() => {
		if(!editingCell || editingCell.field !== 'departments') return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const teacher_info = getTeacherByID(editingCell.teacher_id);
			if(!teacher_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(teacher_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getTeacherByID, cancelCellEdit]);

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		cancelCellEdit();
		setSortedBy(sorted_by);
		setSortAscDesc(sort_asc_desc);
		if(sorted_by === 'departments')
		{
			setDisplayResults(sortByDepartmentDisplay(displayResults, sort_asc_desc));
			return;
		}
		setDisplayResults([...sortedData]);
	};
	
	const handleFilterClick = (selectedFilters = {}) => {
		cancelCellEdit();
		const filtered_data = applySchoolDataFilters(visibleDatabaseData, selectedFilters, schoolDataFilterCategories);
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		setFilteredResults([...filtered_data]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};

	const toggleSelectData = (data_id) => {
		const data_selected_index = selectedData.findIndex((data) => `${data}` === `${data_id}`);

		if(data_selected_index !== -1)
		{
			selectedData.splice(data_selected_index, 1);
		}
		else
		{
			selectedData.push(data_id);
		}

		setSelectedData([...selectedData]);
	};

	const toggleSelectAll = () => {
		const any_data_selected = selectedData.length > 0;
		
		let new_selected_data = [];

		if(!any_data_selected) 
		{
			new_selected_data = displayResults.reduce((results, teacher) => {
				results.push(teacher.teacher_id);
				return results;
			}, []);
		}

		setSelectedData(new_selected_data);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};
	
	const handeDeleteData = async (data_ids) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'teacher', data_ids:data_ids};
		let delete_data_bool = true;
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: "Are you sure?",
				icon: "warning",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete the following teachers:</p>
							<div style={{marginLeft:'50px', textAlign:'left'}}>
								{data_ids.map((data_id, i) => {
									const data_index = databaseData.findIndex((teacher) => `${teacher.teacher_id}` === `${data_id}`);
									const data_info = databaseData[data_index];

									return (
										<div key={i}>{data_info.first_name} {data_info.name}</div>
									);
								})}
							</div>
							<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			const new_database_data = databaseData.filter((data_info) => !data_ids.some((data_id) => `${data_id}` === `${data_info.teacher_id}`));
			const new_filtered_data = filteredResults.filter((filtered_data) => !data_ids.some((data_id) => `${data_id}` === `${filtered_data.teacher_id}`));

			setDatabaseData(new_database_data);
			setFilteredResults(new_filtered_data);
			
			deleteData(data);

			setSelectedData([]);
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) 
		{
			setCurrentDataID(null);
			const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
			const search_value = search_bar ? search_bar.value : '';
			if(search_value.length > 0) handleChange();
		}
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(dataIsLoading || filterActive) return;

		filteredResultsRef.current = Array.isArray(visibleDatabaseData) ? visibleDatabaseData : [];
		setFilteredResults([...filteredResultsRef.current]);
		setHandleChangeComplete(false);
	}, [visibleDatabaseData, dataIsLoading, filterActive]);

	const row_style = {gridTemplateColumns:'35px 1fr 1fr 1fr 1fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditTeacherData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} teachers={databaseData} students={students} courses={courses} classrooms={classrooms} sections={sections} departments={departments} setDatabaseData={setDatabaseData} filteredResults={filteredResults} setFilteredResults={setFilteredResults}/>
		}
		{showUploadModal && !readOnlyAddUpload &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Teachers' uploadAPIName='teacherdata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Teachers</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder="Search for teachers..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
				</div>
				{!readOnlyAddUpload && <div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>}
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div></div>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='teacher_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Last Name <SortToggle handleOnClick={handleSortClick} sortKey='name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>First Name <SortToggle handleOnClick={handleSortClick} sortKey='first_name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Department(s) <SortToggle handleOnClick={handleSortClick} sortKey='departments' passedData={displayResults}/></div>
					{!readOnlyAddUpload ? <div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div> : <div></div>}
				</div>
				{displayResults.length > 0 &&
					<div className='select-all-row'>
						<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						{selectedData.length > 0 &&
							<div className='small-text'>
								<div className='tooltip' data-tooltip-text='Delete teachers' onClick={() => handeDeleteData(selectedData)}>
									<div className='red-link'>{`Delete ${selectedData.length} selected`}</div>
								</div>
							</div>
						}
					</div>
				}
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{Object.keys(scheduleStructureData).length > 0 &&
						<>
						{displayResults.map((data_info) => {
							const teacher_id = data_info.teacher_id;
							const data_selected = selectedData.some((selected_id) => `${selected_id}` === `${teacher_id}`);
							const editing_teacher_id = editingCell?.teacher_id;

							return (
								<div className='school-data-database-row' style={row_style} key={teacher_id}>
									<div className='school-data-row-checkbox-cell' onClick={() => toggleSelectData(teacher_id)}>
										<FontAwesomeIcon className={data_selected ? 'blue-text' : 'dark-gray-text'} icon={data_selected ? faCheckSquare : farSquare}/>
									</div>
									<div
										ref={isEditingField(teacher_id, 'teacher_code') ? activeEditorRef : null}
										className={isEditingField(teacher_id, 'teacher_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_teacher_id ? () => startCellEdit(data_info, 'teacher_code') : undefined}
									>
										{isEditingField(teacher_id, 'teacher_code') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={teacherCodeInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.teacher_code || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('teacher_code', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'teacher_code')}
												/>
											)
										:
											<div>{data_info.teacher_code ? data_info.teacher_code : '--'}</div>
										}
									</div>
									<div
										ref={isEditingField(teacher_id, 'name') ? activeEditorRef : null}
										className={isEditingField(teacher_id, 'name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_teacher_id ? () => startCellEdit(data_info, 'name') : undefined}
									>
										{isEditingField(teacher_id, 'name') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={teacherLastNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.name || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('name', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'name')}
												/>
											)
										:
											<div>{data_info.name}</div>
										}
									</div>
									<div
										ref={isEditingField(teacher_id, 'first_name') ? activeEditorRef : null}
										className={isEditingField(teacher_id, 'first_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_teacher_id ? () => startCellEdit(data_info, 'first_name') : undefined}
									>
										{isEditingField(teacher_id, 'first_name') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<input
													ref={teacherFirstNameInputRef}
													className='school-data-inline-edit-input'
													value={cellDraft?.first_name || ''}
													disabled={isSavingCell}
													onChange={(e) => updateDraftField('first_name', e.target.value)}
													onKeyDown={(e) => handleCellKeyDown(e, data_info)}
													onBlur={() => handleCellBlur(data_info, 'first_name')}
												/>
											)
										:
											<div>{data_info.first_name}</div>
										}
									</div>
									<div
										ref={isEditingField(teacher_id, 'departments') ? activeEditorRef : null}
										className={isEditingField(teacher_id, 'departments') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
										onClick={!editing_teacher_id ? () => startCellEdit(data_info, 'departments') : undefined}
									>
										{isEditingField(teacher_id, 'departments') ?
											(isSavingCell ?
												<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
													<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
												</div>
											:
												<div className='school-data-inline-multiselect-popover'>
													{departments.length === 0 ?
														<div className='medium-text'>No departments available.</div>
													:
														departments.map((department_info) => {
															const department_id = `${department_info.department_id}`;
															const department_selected = cellDraft?.departments?.includes(department_id);

															return (
																<label key={department_id} className='school-data-inline-multiselect-option'>
																	<input
																		type='checkbox'
																		checked={department_selected}
																		disabled={isSavingCell}
																		onChange={() => toggleDepartmentSelection(department_id)}
																	/>
																	<span>{department_info.department}</span>
																</label>
															);
														})
													}
												</div>
											)
										:
											<div>{renderDepartmentDisplay(data_info.departments)}</div>
										}
									</div>
									<div className='school-data-database-row-btn-container'>
										<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(teacher_id)}/>
										<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData([teacher_id])}/>
									</div>
								</div>
							);
						})}
						</>
					}
					</>
				)}
			</div>
		</div>
		</>
	);
}
